import './RiveCoinTypeSwitcher.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Event as RiveEvent } from '@rive-app/canvas';
import { Fit, Layout } from '@rive-app/react-canvas';

import { useRiveMachine } from '../../hooks/rive/useRiveMachine';
import { useLocale } from '../../hooks/useLocale';
import { analytics } from '../../services/analytics';
import soundEffects from '../../services/sounds/soundEffects';
import { RIVE_COIN_TOGGLE_LOCALISATION_MAP } from '../../stores/action-event-trigger/constants';
import { useStores } from '../../stores/useStores';

const url = '/assets/rive/ccsc_toggle.riv';
const STATE_MACHINE_NAME = 'State Machine 1';
const DEFAULT_LOCALIZATION_NUMBER = 1;
const isCCRiveInput = 'isCC';
const languageRiveInput = 'language';
const reminderActiveRiveInput = 'reminder_active';
const crownIsBlackRiveInput = 'crown_isBlack';
const RIVE_PLAY_TIMEOUT_MS = 150;

function Crown({ onThemeChange }: { onThemeChange?: (theme: 'dark' | 'light') => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [currentTheme, setCurrentTheme] = useState<'dark' | 'light'>('dark');
  const themeRef = useRef(currentTheme);

  useEffect(() => {
    themeRef.current = currentTheme;
  }, [currentTheme]);

  const checkTheme = useCallback(() => {
    if (!ref.current) {
      return 'dark';
    }
    if (ref.current.classList.contains('_white')) {
      return 'light';
    }
    let parent = ref.current?.parentElement;
    if (!parent) {
      return 'dark';
    }
    while (parent) {
      if (
        parent.classList.contains('game-header') ||
        parent.classList.contains('scrolled') ||
        parent.classList.contains('full-page-dialog-open')
      ) {
        return 'light';
      }
      parent = parent.parentElement;
    }
    return 'dark';
  }, []);

  useEffect(() => {
    if (onThemeChange) {
      onThemeChange(currentTheme);
    }
  }, [currentTheme, onThemeChange]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const nextTheme = checkTheme();
      if (nextTheme !== themeRef.current) {
        setCurrentTheme(nextTheme);
      }
    });
    let parent = ref.current?.parentElement;
    while (parent) {
      observer.observe(parent, { attributes: true, attributeFilter: ['class'] });
      parent = parent.parentElement;
    }
    return () => observer.disconnect();
  }, [checkTheme]);

  return <div ref={ref} className="rive-coin-type-switcher-crown__decoration" />;
}

export function RiveCoinTypeSwitcher() {
  const [loaded, setLoaded] = useState<boolean>(false);
  const { locale } = useLocale();
  const localizationNumber = RIVE_COIN_TOGGLE_LOCALISATION_MAP.get(locale) || DEFAULT_LOCALIZATION_NUMBER;
  const { actionEventTriggerStore, coinTypeStore } = useStores();
  const { coinType } = coinTypeStore;
  const playTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onStateChange = useCallback((e: RiveEvent) => {
    if (e.type === 'statechange') {
      setLoaded(true);
    }
  }, []);

  const onRiveEvent = useCallback(
    (e: RiveEvent) => {
      const event = e.data as { name: string };
      if (event?.name === 'SC') {
        void analytics.sendEvent('sc_toggle', {
          event_category: 'mini_tutorial',
          event_action: 'click',
          lobby_page: 'CC',
          page_url: null,
        });
        actionEventTriggerStore.finishCoinTutorial();
      }
    },
    [actionEventTriggerStore],
  );

  const { rive, RiveComponent } = useRiveMachine({
    src: url || '',
    stateMachineName: STATE_MACHINE_NAME,
    layout: new Layout({
      fit: Fit.Contain,
    }),
    onRiveEvent,
    onStateChange,
    autoplay: true,
  });

  const isCCInput = useMemo(() => {
    return rive?.stateMachineInputs(STATE_MACHINE_NAME)?.find((i) => i.name === isCCRiveInput);
  }, [rive]);

  const languageInput = useMemo(() => {
    return rive?.stateMachineInputs(STATE_MACHINE_NAME)?.find((i) => i.name === languageRiveInput);
  }, [rive]);

  const reminderInput = useMemo(() => {
    return rive?.stateMachineInputs(STATE_MACHINE_NAME)?.find((i) => i.name === reminderActiveRiveInput);
  }, [rive]);

  const isDarkInput = useMemo(() => {
    return rive?.stateMachineInputs(STATE_MACHINE_NAME)?.find((i) => i.name === crownIsBlackRiveInput);
  }, [rive]);

  const stop = useCallback(() => {
    if (reminderInput) {
      reminderInput.value = false;
    }
  }, [reminderInput]);

  useEffect(() => {
    if (loaded) {
      actionEventTriggerStore.setCoinAnimationRef({ stop });
      playTimeoutRef.current = setTimeout(() => {
        if (languageInput) {
          languageInput.value = localizationNumber;
        }
        if (reminderInput) {
          reminderInput.value = true;
        }
        void analytics.sendEvent('sc_toggle', {
          event_category: 'mini_tutorial',
          event_action: 'impression',
          lobby_page: 'CC',
          page_url: null,
        });
      }, RIVE_PLAY_TIMEOUT_MS);
    }
    return () => {
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
        actionEventTriggerStore.setCoinAnimationRef(null);
      }
    };
  }, [actionEventTriggerStore, languageInput, loaded, localizationNumber, reminderInput, stop]);

  const onThemeChange = useCallback(
    (theme: 'dark' | 'light') => {
      if (isDarkInput) {
        isDarkInput.value = theme === 'dark';
      }
    },
    [isDarkInput],
  );

  const switchCoinType = useCallback(() => {
    coinTypeStore.setCoinType('SC');
    soundEffects.playSound('toggle');
    if (isCCInput) {
      isCCInput.value = false;
    }
  }, [coinTypeStore, isCCInput]);

  return (
    <div className="rive-coin-type-switcher-container">
      <div className="rive-coin-type-switcher-animation-container">
        <RiveComponent />
      </div>
      <button
        aria-label={`Switch coin type to ${coinType === 'GC' ? 'SC' : 'GC'}`}
        className="rive-coin-type-switcher-toggle"
        type="button"
        onClick={() => switchCoinType()}
      />
      <Crown onThemeChange={onThemeChange} />
    </div>
  );
}
